import React, { useMemo, createContext, useState, useContext, useCallback } from 'react'
import { PaginationReq, ThirdPartyDepositSlipQuery, AdminDepositStatusType, DepositMethodType, ThirdPartyDepositCourierSimple, CashType, AgentType } from '@golden/gdk-admin'
import { omitBy, isUndefined } from '@golden/utils'
import { isValid, getTime } from 'date-fns'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import MuiButton from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import FormStateProvider from '../../default/form/FormStateProvider'
import FormField from '../../default/form/FormField'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { pipe, guaranteeNotUndefined, parseInt, guaranteeBetween, acceptUndefined, getValueFromChangeEvent, guaranteeBeKey, parseFloat, convertEmptyToUndefined, getValueFromValue, getCashInputProps, parseArray, getTimeFromDateInputValue } from '../../../utils/default/FormHelper'
import allRoutes from '../route/route'
import { useRequestFromSearch, SearchToRequestFunc, InitialFormFunc, useChangeUrlSubmit, useGetData } from '../../../utils/default/ComplexFlowHook'
import { FormValidation, createDefaultFormState, ValueGetter } from '../../../utils/default/FormHook'
import adminDepositStatusName from '../../../constants/admin/adminDepositStatusName'
import depositTypeName, { manualDepositTypeName } from '../../../constants/default/depositTypeName'
import cashTypeName from '../../../constants/default/cashTypeName'
import { createValidateCash, createValidateMin, createValidateMax } from '../../../utils/default/Validator'
import RequiredText from '../../default/form/RequiredText'
import NumberInput from '../../default/form/NumberInput'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'
import agentTypeName from '../../../constants/default/agentTypeName'
import MultipleSelector, { PropTypes as MultipleSelectorProps } from '../../../components/default/form/MultipleSelector'
import { createShouldDisableFuture, getCorrectedDateRange, setToday } from '@golden/shared'
import DateInputBase, { DateInputValue } from '../../default/form/DateInput'

export type SlipRequest = PaginationReq & ThirdPartyDepositSlipQuery

export interface SlipFormType {
  account: string
  serial: string
  status: '--' | AdminDepositStatusType
  forwarderIds: number[]
  methods: DepositMethodType[]
  createdAt: DateInputValue
  finishedAt: DateInputValue
  cashType: CashType
  min: string
  max: string
  layer_ids: number[]
  agent_types: AgentType[]
  real_name: string
  sub_wallet_address: string
  from_address: string
}

export const searchToRequest: SearchToRequestFunc<SlipRequest> = (search) => {
  const converted = {
    ...search,
    page: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.page),
    status: acceptUndefined(search.status, pipe(
      (value) => guaranteeBeKey(value, Object.keys(adminDepositStatusName)),
      parseInt
    )),
    forwarder_ids: acceptUndefined(
      search.forwarder_ids,
      pipe(
        parseArray,
        (values) => values.map((item: string) => Number(item))
      )
    ),
    methods: acceptUndefined(
      search.methods,
      pipe(
        parseArray,
        (values) => values.map((item: string) => {
          guaranteeBeKey(item, Object.keys(depositTypeName))
          return item as DepositMethodType
        })
      )
    ),
    created_at_start: acceptUndefined(search.created_at_start, pipe(
      parseInt
    )),
    created_at_end: acceptUndefined(search.created_at_end, pipe(
      parseInt
    )),
    finished_at_start: acceptUndefined(search.finished_at_start, pipe(
      parseInt
    )),
    finished_at_end: acceptUndefined(search.finished_at_end, pipe(
      parseInt
    )),
    cash_start: acceptUndefined(search.cash_start, parseFloat),
    cash_end: acceptUndefined(search.cash_end, parseFloat),
    layer_ids: acceptUndefined(
      search.layer_ids,
      pipe(
        parseArray,
        (values) => values.map((item: string) => Number(item))
      )
    ),
    agent_types: acceptUndefined(
      search.agent_types,
      pipe(
        parseArray,
        (values) => values.map((item: string) => Number(item) as AgentType)
      )
    ),
    sub_wallet_address: search.sub_wallet_address,
    from_address: search.from_address
  } as SlipRequest
  if (converted.created_at_start && converted.created_at_end && converted.created_at_end < converted.created_at_start) throw new Error('The end time can\'t exceed the start time')
  if (converted.finished_at_start && converted.finished_at_end && converted.finished_at_end < converted.finished_at_start) throw new Error('The end time can\'t exceed the start time')
  if (converted.cash_start && converted.cash_end && converted.cash_end < converted.cash_start) throw new Error('The cash_end can\'t exceed the cash_start')
  return omitBy(converted, isUndefined) as SlipRequest
}

const EMPTY_FIELD_DISPLAY = '-'

export function formatAddress (value: string | null): string {
  return value || EMPTY_FIELD_DISPLAY
}

export const initialForm: InitialFormFunc<SlipFormType> = (defaultForm) => ({
  account: '',
  serial: '',
  status: '--',
  forwarderIds: [],
  methods: [],
  createdAt: setToday(),
  finishedAt: {
    start: null,
    end: null
  },
  cashType: CashType.RMB,
  min: '',
  max: '',
  layer_ids: [],
  agent_types: [],
  real_name: '',
  sub_wallet_address: '',
  from_address: '',
  ...defaultForm
})

const formToRequest = (form: SlipFormType): SlipRequest => {
  const converted = {
    account: convertEmptyToUndefined(form.account),
    order_number: convertEmptyToUndefined(form.serial),
    status: form.status === '--' ? undefined : form.status,
    forwarder_ids: form.forwarderIds.length === 0 ? undefined : form.forwarderIds,
    methods: form.methods.length === 0 ? undefined : form.methods,
    created_at_start: form.createdAt.start && isValid(form.createdAt.start) ? getTime(form.createdAt.start) : undefined,
    created_at_end: form.createdAt.end && isValid(form.createdAt.end) ? getTime(form.createdAt.end) : undefined,
    finished_at_start: form.finishedAt.start && isValid(form.finishedAt.start) ? getTime(form.finishedAt.start) : undefined,
    finished_at_end: form.finishedAt.end && isValid(form.finishedAt.end) ? getTime(form.finishedAt.end) : undefined,
    cash_type: form.cashType,
    cash_start: convertEmptyToUndefined(form.min),
    cash_end: convertEmptyToUndefined(form.max),
    layer_ids: form.layer_ids.length === 0 ? undefined : form.layer_ids,
    agent_types: form.agent_types.length === 0 ? undefined : form.agent_types,
    real_name: convertEmptyToUndefined(form.real_name),
    sub_wallet_address: convertEmptyToUndefined(form.sub_wallet_address),
    from_address: convertEmptyToUndefined(form.from_address),
    page: 1
  } as SlipRequest
  return omitBy(converted, isUndefined) as SlipRequest
}

const getValueFromEvent: ValueGetter<SlipFormType> = {
  account: getValueFromChangeEvent,
  serial: getValueFromChangeEvent,
  status: getValueFromChangeEvent,
  forwarderIds: getValueFromValue,
  methods: getValueFromValue,
  createdAt: getTimeFromDateInputValue,
  finishedAt: getTimeFromDateInputValue,
  cashType: getValueFromChangeEvent,
  min: getValueFromChangeEvent,
  max: getValueFromChangeEvent,
  layer_ids: getValueFromValue,
  agent_types: getValueFromValue,
  real_name: getValueFromChangeEvent,
  sub_wallet_address: getValueFromChangeEvent,
  from_address: getValueFromChangeEvent
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const Button = React.memo(MuiButton)
const TextField = React.memo(MuiTextField)

const cashInputProps = getCashInputProps()

type DateType = 'createdAt' | 'finishedAt'
const DateInput: React.FC<{ dateType: DateType, startLabel?: string, endLabel?: string }> = React.memo((props) => {
  const { dateType, startLabel, endLabel } = props
  const { value, handleChange } = useContext(FormContext)
  const { t } = useT()
  const time = useMemo(() => ({ start: value[dateType].start, end: value[dateType].end }), [value[dateType]])
  const {
    shouldDisableStartDate,
    shouldDisableEndDate
  } = useMemo(() => createShouldDisableFuture(), [])
  const startOption = useMemo(() => ({
    label: startLabel ?? t('common.beginAt'),
    shouldDisableDate: shouldDisableStartDate
  }), [shouldDisableStartDate, t])
  const endOption = useMemo(() => ({
    label: endLabel ?? t('common.endAt'),
    shouldDisableDate: shouldDisableEndDate
  }), [shouldDisableEndDate, t])
  const onChange = useCallback((value) => {
    handleChange(dateType)(getCorrectedDateRange(value))
  }, [])
  return <DateInputBase
    sm
    value={time}
    onChange={onChange}
    start={startOption}
    end={endOption}
  />
})

const DepositThirdPartySlipForm: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const pageFlow = usePageFlow()
  const layers = useGDKStore.player.layers()
  const [couriers, setCouriers] = useState<ThirdPartyDepositCourierSimple[]>([])

  useGetData({
    gdkFunc: () => gdk.deposit.getCourierList(),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setCouriers(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const request = useRequestFromSearch({ searchToRequest })

  const defaultForm = useMemo(() => {
    if (request) {
      return initialForm(omitBy({
        account: request.account,
        serial: request.order_number,
        status: request.status,
        forwarderIds: request.forwarder_ids ?? [],
        methods: request.methods ?? [],
        createdAt: {
          start: request.created_at_start ? new Date(request.created_at_start) : null,
          end: request.created_at_end ? new Date(request.created_at_end) : null
        },
        finishedAt: {
          start: request.finished_at_start ? new Date(request.finished_at_start) : null,
          end: request.finished_at_end ? new Date(request.finished_at_end) : null
        },
        cashType: request.cash_type,
        min: request.cash_start?.toString() ?? '',
        max: request.cash_end?.toString() ?? '',
        layer_ids: request.layer_ids ?? [],
        agent_types: request.agent_types ?? [],
        real_name: request.real_name,
        sub_wallet_address: request.sub_wallet_address,
        from_address: request.from_address
      }, isUndefined))
    }
    return initialForm()
  }, [request])

  const handleSubmit = useChangeUrlSubmit({
    toAddNowTimestamp: true,
    formToRequest,
    encodePath: allRoutes.depositThirdPartySlip.encodePath
  })

  const validation = useMemo(() => ({
    account: [],
    serial: [],
    status: [],
    forwarderIds: [],
    methods: [],
    createdAt: [],
    finishedAt: [],
    cashType: [],
    min: [
      {
        func: createValidateCash('min', t),
        when: ['change', 'beforeClickSubmit']
      },
      {
        func: createValidateMin('min', 'max', t),
        when: ['change', 'beforeClickSubmit']
      }
    ],
    max: [
      {
        func: createValidateCash('max', t),
        when: ['change', 'beforeClickSubmit']
      },
      {
        func: createValidateMax('min', 'max', t),
        when: ['change', 'beforeClickSubmit']
      }
    ],
    layer_ids: [],
    agent_types: [],
    real_name: [],
    sub_wallet_address: [],
    from_address: []
  } as FormValidation<SlipFormType>), [t])

  const statusOptions = [{ name: t('common.all'), value: '--' }].concat(
    Object.keys(adminDepositStatusName)
      .map((key) => Number(key) as AdminDepositStatusType)
      .map((key) => ({
        name: t(adminDepositStatusName[key]),
        value: key
      })) as any
  )

  const courierOptions = couriers.map((courier) => ({
    name: courier.name,
    value: courier.id
  }))

  const methodOptions = Object.keys(depositTypeName)
    .filter((key) => key !== DepositMethodType.CGPAY_USDT && !Object.keys(manualDepositTypeName).includes(key))
    .map((key) => ({ name: t(depositTypeName[key as DepositMethodType]), value: key }))

  const playerLayerOptions = useMemo(() => layers.map((item) => ({ name: item.name, value: item.id })), [layers])

  const agentLayerOptions = useMemo(() => Object.keys(agentTypeName)
    .map((key) => Number(key) as AgentType).map((value) => ({ name: t(agentTypeName[value]), value })), [t])

  const cashTypeOptions = Object.entries(cashTypeName).map(([value, name]) => ({ name, value }))

  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultForm}
      onSubmit={handleSubmit}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
    >
      <Paper>
        <Box padding={4}>
          <LoadingAndErrorFrame {...pageFlow.status}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Box
                  paddingY={1.25}
                  paddingX={2}
                  className={classes.pinkTitleBar}
                >
                  <Typography variant="h5">
                    {t('page.thirdPartyDepositSlip')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="account"
                      label={t('common.account')}
                      placeholder={t('placeholder.inputAccount')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="serial"
                      label={t('common.orderNumber')}
                      placeholder={t('placeholder.inputOrderNumber')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, DropDownProps>
                      context={FormContext}
                      component={DropDown}
                      name="status"
                      options={statusOptions}
                      label={t('common.orderStatus2')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, MultipleSelectorProps>
                      context={FormContext}
                      component={MultipleSelector}
                      name="forwarderIds"
                      options={courierOptions}
                      label={t('common.courierName')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, MultipleSelectorProps>
                      context={FormContext}
                      component={MultipleSelector}
                      name="methods"
                      label={t('common.depositWay')}
                      options={methodOptions}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="real_name"
                      label={t('common.depositName')}
                      placeholder={t('placeholder.inputDepositName')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="sub_wallet_address"
                      label={t('common.subWallet')}
                      placeholder={t('placeholder.inputSubWallet')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="from_address"
                      label={t('common.transferAddress')}
                      placeholder={t('placeholder.inputTransferAddress')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DateInput
                      dateType="createdAt"
                      startLabel={t('common.applyTime') + t('common.beginAt')}
                      endLabel={t('common.applyTime') + t('common.endAt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateInput
                      dateType="finishedAt"
                      startLabel={t('common.confirmTime') + t('common.beginAt')}
                      endLabel={t('common.confirmTime') + t('common.endAt')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={6} lg={3}>
                    <FormField<SlipFormType, DropDownProps>
                      context={FormContext}
                      component={DropDown}
                      name="cashType"
                      label={t('common.orderMoney')}
                      options={cashTypeOptions}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={NumberInput}
                      name="min"
                      label={t('common.minOrderMoney')}
                      placeholder={t('placeholder.inputMinOrderMoney')}
                      inputProps={cashInputProps}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormField<SlipFormType, TextFieldProps>
                      context={FormContext}
                      component={NumberInput}
                      name="max"
                      label={t('common.maxOrderMoney')}
                      placeholder={t('placeholder.inputMaxOrderMoney')}
                      inputProps={cashInputProps}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={6} lg={3}>
                    <FormField<SlipFormType, MultipleSelectorProps>
                      context={FormContext}
                      component={MultipleSelector}
                      name="layer_ids"
                      label={t('common.playerLayer')}
                      options={playerLayerOptions}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <FormField<SlipFormType, MultipleSelectorProps>
                      context={FormContext}
                      component={MultipleSelector}
                      name="agent_types"
                      label={t('common.agentLayer')}
                      options={agentLayerOptions}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <RequiredText />
              </Grid>
              <Grid item>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <FormSubmitButton
                      component={Button}
                      context={FormContext}
                      type="submit"
                      className={classes.purpleGradualButton}
                    >
                      {t('common.search')}
                    </FormSubmitButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </LoadingAndErrorFrame>
        </Box>
      </Paper>
    </FormStateProvider>
  )
}

export default React.memo(DepositThirdPartySlipForm)
